import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Pagination,
  alpha,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Add as AddIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { GetFilterUser, DeleteRoommateReq } from '../../services/advert';
import { GetCountries, GetCities, GetDistrict } from '../../services/location';
import AdvertisementCard from '../../components/Advertisement/AdvertisementCard';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import PageShell, { SurfaceCard } from '../../components/common/PageShell';

const MyAdvertisements = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [filters, setFilters] = useState({
    country: '',
    city: '',
    district: '',
    ageStart: '',
    ageEnd: '',
    rentStart: '',
    rentEnd: '',
    gender: '',
    petsAllowed: false,
    smokingAllowed: false,
    alcoholAllowed: false,
    utilitiesIncluded: false,
  });

  const [locations, setLocations] = useState({
    countries: [],
    cities: [],
    districts: [],
  });

  const [sortBy, setSortBy] = useState({ field: 'CreateDate', order: 'desc' });

  useEffect(() => {
    fetchListings();
    fetchCountries();
  }, [page, sortBy]);

  const createFilters = () => {
    const filterArray = [];
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        if (key.includes('Start')) {
          filterArray.push({
            propertyName: key.replace('Start', ''),
            operation: '>=',
            value: value.toString(),
          });
        } else if (key.includes('End')) {
          filterArray.push({
            propertyName: key.replace('End', ''),
            operation: '<=',
            value: value.toString(),
          });
        } else {
          filterArray.push({
            propertyName: key,
            operation: '==',
            value: value.toString(),
          });
        }
      }
    });
    return filterArray;
  };

  const fetchListings = async () => {
    setIsLoading(true);
    try {
      const response = await GetFilterUser({
        filters: createFilters(),
        sortBy: sortBy.field,
        sortOrder: sortBy.order,
        pageIndex: page,
        pageSize: 12,
      });

      if (response.statusCode === 200) {
        setListings(response.data.items);
        setHasMore(response.data.hasNextPage);
        setError(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await GetCountries();
      if (response.statusCode === 200) {
        setLocations((prev) => ({ ...prev, countries: response.data }));
      }
    } catch (err) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = (id) => {
    setSelectedItemId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await DeleteRoommateReq(selectedItemId);
      if (response.statusCode === 200) {
        toast.success(t('myAdvertisements.deleteSuccess'));
        setListings(listings.filter((item) => item.id !== selectedItemId));
      } else {
        toast.error(t('myAdvertisements.deleteError'));
      }
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setDeleteDialogOpen(false);
      setSelectedItemId(null);
    }
  };

  return (
    <PageShell
      title={t('myAdvertisements.title')}
      subtitle={t('myAdvertisements.subtitle') || t('myAdvertisements.noListings')}
      icon={<ListAltIcon />}
      actions={
        <>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {t('advertisement.filters.title')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/add-roommate')}
          >
            {t('myAdvertisements.addNew')}
          </Button>
        </>
      }
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress />
        </Box>
      ) : error ? (
        <SurfaceCard sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
        </SurfaceCard>
      ) : listings.length > 0 ? (
        <>
          <Grid container spacing={2.5}>
            {listings.map((item) => (
              <Grid item key={item.id} xs={12} sm={6} md={4}>
                <AdvertisementCard
                  item={item}
                  onEdit={() => navigate(`/edit-roommate/${item.id}`)}
                  onDelete={() => handleDelete(item.id)}
                />
              </Grid>
            ))}
          </Grid>

          <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
            <Pagination
              count={Math.max(1, Math.ceil((listings.length || 12) / 12))}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            />
          </Stack>
        </>
      ) : (
        <SurfaceCard sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3,
              mx: 'auto',
              mb: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            <ListAltIcon />
          </Box>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('myAdvertisements.noListings')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/add-roommate')}
            sx={{ mt: 1.5 }}
          >
            {t('myAdvertisements.createFirst')}
          </Button>
        </SurfaceCard>
      )}

      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="sm" />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </PageShell>
  );
};

export default MyAdvertisements;
